import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import routes from "../../routes/routes";
import { Wrapper, Card, Title, Input, ErrorMsg, LeftImage, RightPanel } from "./Login.style";
import { BtnGeneral } from "../../styles/Buttons";
import icons from "../../assets/icons/iconMap";
import styled, { keyframes } from "styled-components";

/* ── Animaciones ─────────────────────────────────────────────────────────── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Contenedor de sedes ─────────────────────────────────────────────────── */
const SedesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #c5d5ea; border-radius: 10px; }
`;

/* ── Tarjeta de sede ─────────────────────────────────────────────────────── */
const SedeCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1.5px solid #e0eaf5;
  border-radius: 12px;
  background: #f8fbff;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.35s ease both;
  animation-delay: ${(props) => props.$delay || "0s"};

  &:hover {
    border-color: #1e3c72;
    background: #eaf2ff;
    transform: translateX(4px);
    box-shadow: 0 4px 14px rgba(30, 60, 114, 0.12);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SedeIconWrap = styled.div`
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const SedeInfo = styled.div`
  flex: 1;
`;

const SedeNombre = styled.p`
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e2e4a;
  line-height: 1.3;
`;

const SedeSubtitle = styled.p`
  margin: 2px 0 0;
  font-size: 0.75rem;
  color: #8099bc;
`;

const SedeArrow = styled.span`
  font-size: 1.1rem;
  color: #b0c4de;
  transition: color 0.2s, transform 0.2s;

  ${SedeCard}:hover & {
    color: #1e3c72;
    transform: translateX(3px);
  }
`;

/* ── Subtitle debajo del título ──────────────────────────────────────────── */
const Subtitle = styled.p`
  margin: -12px 0 6px;
  color: #9aabbf;
  font-size: 0.85rem;
`;

const WideCard = styled(Card)`
  width: 420px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px 10px 38px;
  border: 1.5px solid #e0eaf5;
  border-radius: 10px;
  font-size: 0.88rem;
  background: #f4f8ff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238099bc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E") no-repeat 12px center;
  color: #1e2e4a;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #1e3c72;
    background-color: #fff;
  }

  &::placeholder { color: #b0bfd4; }
`;

/* ══════════════════════════════════════════════════════════════════════════ */
export default function Login() {
  const { user, login, sedes, selectSede, sedeActiva, loading, getDefaultRoute } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState("login");
  const [filtro, setFiltro] = useState("");
  const [targetRoute, setTargetRoute] = useState(routes.home);

  // Si el usuario ya está autenticado (o llega redirigido por ProtectedRoute)
  useEffect(() => {
    if (!loading && user) {
      const dest = getDefaultRoute();
      setTargetRoute(dest);

      if (sedes && sedes.length > 1 && !sedeActiva) {
        setStep("sede");
      } else if (sedeActiva || (sedes && sedes.length === 1)) {
        if (!sedeActiva && sedes && sedes.length === 1) {
          selectSede(sedes[0]);
        }
        navigate(dest, { replace: true });
      }
    }
  }, [user, sedes, sedeActiva, loading, getDefaultRoute, navigate, selectSede]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    try {
      const res = await login(username, password);
      const sedesDisponibles = Array.isArray(res) ? res : (res?.sedes || []);
      const dest = res?.defaultRoute || getDefaultRoute() || routes.home;
      setTargetRoute(dest);

      if (sedesDisponibles && sedesDisponibles.length === 1) {
        selectSede(sedesDisponibles[0]);
        navigate(dest, { replace: true });
      } else if (sedesDisponibles && sedesDisponibles.length > 1) {
        setStep("sede");
      } else {
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || "Usuario o contraseña incorrectos");
    }
  }

  function handleSelectSede(sede) {
    selectSede(sede);
    const dest = targetRoute || getDefaultRoute() || routes.home;
    navigate(dest, { replace: true });
  }

  /* ── Pantalla 2: Selección de Sede ─────────────────────────────────────── */
  if (step === "sede") {
    return (
      <Wrapper>
        <LeftImage />
        <RightPanel>
          <WideCard>
            <Title>
              🏥 Selecciona tu Sede
            </Title>
            <Subtitle>
              {sedes.length
                ? `Tienes acceso a ${sedes.length} sede${sedes.length > 1 ? "s" : ""}`
                : "Sin sedes asignadas"}
            </Subtitle>

            {sedes.length === 0 ? (
              <ErrorMsg>No tienes sedes asignadas. Contacta al administrador.</ErrorMsg>
            ) : (() => {
              const filtradas = sedes
                .filter((s) =>
                  s.nombreSede.toLowerCase().includes(filtro.toLowerCase()) ||
                  (s.ciudad || "").toLowerCase().includes(filtro.toLowerCase())
                )
                .sort((a, b) => {
                  const ordenA = a.orden != null ? Number(a.orden) : Infinity;
                  const ordenB = b.orden != null ? Number(b.orden) : Infinity;
                  if (ordenA !== ordenB) return ordenA - ordenB;
                  return (a.nombreSede || "").localeCompare(b.nombreSede || "");
                });
              return (
                <>
                  <SearchInput
                    type="text"
                    placeholder="Buscar sede o ciudad..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    autoFocus
                  />
                  <SedesGrid>
                    {filtradas.length === 0 ? (
                      <SedeSubtitle style={{ padding: "14px 0", textAlign: "center" }}>
                        No se encontraron sedes con "{filtro}"
                      </SedeSubtitle>
                    ) : (
                      filtradas.map((sede, i) => (
                        <SedeCard
                          key={sede.id}
                          type="button"
                          onClick={() => handleSelectSede(sede)}
                          $delay={`${i * 60}ms`}
                        >
                          <SedeIconWrap>🏥</SedeIconWrap>
                          <SedeInfo>
                            <SedeNombre>{sede.nombreSede}</SedeNombre>
                            <SedeSubtitle>
                              {sede.nombreEmpresa}
                              {sede.ciudad ? ` — ${sede.ciudad}` : ""}
                            </SedeSubtitle>
                          </SedeInfo>
                          <SedeArrow>›</SedeArrow>
                        </SedeCard>
                      ))
                    )}
                  </SedesGrid>
                </>
              );
            })()}
          </WideCard>
        </RightPanel>
      </Wrapper>
    );
  }

  /* ── Pantalla 1: Login ──────────────────────────────────────────────────── */
  return (
    <Wrapper>
      <LeftImage />
      <RightPanel>
        <Card>
          <Title>
            <img src={icons["login"]} alt="Login" style={{ width: 50, height: 50, verticalAlign: "bottom" }} />
            Iniciar Sesión
          </Title>

          <form onSubmit={handleSubmit}>
            <Input
              type="text"
              placeholder="Usuario o Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <BtnGeneral type="submit" bg="blue" w="100%" padding="13px" radius="6px" fs="1rem" fw="600" mgt="1rem">
              Entrar
            </BtnGeneral>
          </form>

          {error && <ErrorMsg>{error}</ErrorMsg>}
        </Card>
      </RightPanel>
    </Wrapper>
  );
}
